import { TestCardContainer, TestVerifyResultStyles } from '@Src/_basic/components/MuiStyleComponent/TestComponent';
import ColFieldInfo from '@Src/_basic/components/YatungTest/ColFieldInfo';
import EditButtons from '@Src/_basic/components/YatungTest/EditButtons';
import TestTable from '@Src/_basic/components/YatungTest/TestTable';
import { checkIsNaN } from '@Src/_basic/helpers/CheckIsNaN';
import { formatNumber } from '@Src/_basic/helpers/FormatNumber';
import { basicNumberSchema } from '@Src/_basic/helpers/TestForm';
import { AllTest } from '@Src/_basic/object/TestType';
import {
  ChemicalSolidContent,
  ImportChemicalSolidContent,
  PutChemicalSolidContentRequest,
} from '@Src/_basic/object/test/cementtitiousLiquid/ChemicalSolidContentType';
import { TestApi } from '@Src/_basic/protocol/test/TestApi';
import LabelInput from '@Src/app/module/proportionManage/components/LabelInput';
import { useFormik } from 'formik';
import React, { ChangeEvent, memo, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';

interface Props {
  testDetail: ChemicalSolidContent;
  // eslint-disable-next-line no-unused-vars
  afterUpdateTest: (test: AllTest) => void;
  canEdit?: boolean;
}

const validationSchema = Yup.object().shape({
  solidContent: basicNumberSchema,
});

function ExportChemicalSolidContentTable({ testDetail, afterUpdateTest, canEdit }: Props) {
  const { t: i18T } = useTranslation();
  const [edit, setEdit] = useState<boolean>(false);

  const initialValues = useMemo(
    () => ({
      solidContent: testDetail.solidContent,
    }),
    [testDetail],
  );

  const firstHeader = useMemo(
    () => ({
      title: `${i18T(`TEST.CHEMICAL_SOLID_CONTENT.SOLID_CONTENT`)}(%)`,
    }),
    [i18T],
  );

  const firstInfo = useMemo(
    () => [
      {
        title: `${formatNumber(testDetail?.solidContent?.toFixed(4))}`,
      },
    ],
    [testDetail?.solidContent],
  );

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: (values) => {
      const data: ImportChemicalSolidContent = {
        solidContent: checkIsNaN(+values.solidContent),
      };

      TestApi.putTest<PutChemicalSolidContentRequest>(
        {
          exptItemCode: testDetail.exptItemCode,
          fields: {
            ...testDetail,
            ...data,
          },
        },
        (_data) => {
          afterUpdateTest(_data);
          setEdit(false);
        },
        (err) => console.log(err),
      );
    },
  });

  const solidContentView = useMemo(() => {
    switch (edit) {
      case true:
        return (
          <ColFieldInfo
            titleStyle={{ ...TestVerifyResultStyles.resultTitle, borderBottom: '1px solid #ccc', paddingBottom: 20 }}
            title={`${i18T(`TEST.RECYCLE_CHLORIDE.CHLORIDE`)}(%)`}
          >
            <LabelInput
              valueWidth="150px"
              value={formik.values.solidContent}
              isError={!!(formik.touched.solidContent && formik.errors.solidContent)}
              onChange={(e: ChangeEvent<HTMLInputElement>) => formik.setFieldValue('solidContent', e.target.value)}
            />
          </ColFieldInfo>
        );
      case false:
        return <TestTable headerData={firstHeader} rows={firstInfo} />;
      default:
        break;
    }
  }, [edit, formik, firstInfo, firstHeader, i18T]);

  return (
    <TestCardContainer>
      <EditButtons edit={edit} setEdit={setEdit} onSubmit={formik.handleSubmit} canEdit={canEdit} />
      {solidContentView}
    </TestCardContainer>
  );
}

export default memo(ExportChemicalSolidContentTable);
